// frontend/modules/customer-loan-info/components/LoanInfoDisplay.tsx
import React from 'react';
import { Card, Descriptions, Typography, Tag, Spin, Alert, Row, Col, Statistic, Table, Divider } from 'antd';
import { 
  DollarOutlined, 
  CalendarOutlined, 
  PercentageOutlined, 
  BankOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined 
} from '@ant-design/icons';
import dayjs from 'dayjs';
import {CustomerLoanInformationDTO} from "@/shared/api/model";

const { Title, Text } = Typography;

interface LoanInfoDisplayProps {
  loanInfo?: CustomerLoanInformationDTO;
  loading?: boolean;
  error?: any;
}

export const LoanInfoDisplay: React.FC<LoanInfoDisplayProps> = ({
  loanInfo,
  loading = false,
  error
}) => {
  if (loading) {
    return (
      <Card>
        <div style={{ textAlign: 'center', padding: '40px' }}>
          <Spin size="large" />
          <div style={{ marginTop: '16px' }}>Loading loan information...</div>
        </div>
      </Card>
    );
  }

  if (error) {
    return (
      <Alert
        message="Error Loading Loan Information"
        description={error?.message || 'Failed to load customer loan information'}
        type="error"
        showIcon
      />
    );
  }

  if (!loanInfo) {
    return (
      <Alert
        message="No Loan Information"
        description="No loan information found for the specified parameters"
        type="info"
        showIcon
      />
    );
  }

  const { basicInfo, balanceSummary, arrearsInfo, accruedInfo, paymentHistory, scheduleInfo } = loanInfo;

  // Payment history table columns
  const paymentColumns = [
    {
      title: 'Date',
      dataIndex: 'paymentDate',
      key: 'paymentDate',
      render: (date: string) => dayjs(date).format('DD/MM/YYYY'),
    },
    {
      title: 'Amount',
      dataIndex: 'amount',
      key: 'amount',
      render: (amount: number) => `${amount?.toLocaleString() || 0}`,
    },
    {
      title: 'Type',
      dataIndex: 'paymentType',
      key: 'paymentType',
    },
    {
      title: 'Reference',
      dataIndex: 'reference',
      key: 'reference',
    },
  ];

  // Schedule table columns
  const scheduleColumns = [
    {
      title: 'Due Date',
      dataIndex: 'dueDate',
      key: 'dueDate',
      render: (date: string) => dayjs(date).format('DD/MM/YYYY'),
    },
    {
      title: 'Principal',
      dataIndex: 'principalAmount',
      key: 'principalAmount',
      render: (amount: number) => `${amount?.toLocaleString() || 0}`,
    },
    {
      title: 'Interest',
      dataIndex: 'interestAmount',
      key: 'interestAmount',
      render: (amount: number) => `${amount?.toLocaleString() || 0}`,
    },
    {
      title: 'Total',
      dataIndex: 'totalAmount',
      key: 'totalAmount',
      render: (amount: number) => `${amount?.toLocaleString() || 0}`,
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status: string) => (
        <Tag color={status === 'PAID' ? 'green' : status === 'OVERDUE' ? 'red' : 'orange'}>
          {status}
        </Tag>
      ),
    },
  ];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
      {/* Basic Loan Information */}
      <Card title={<><BankOutlined /> Basic Loan Information</>}>
        <Descriptions bordered column={2}>
          <Descriptions.Item label="Branch">{basicInfo?.branch}</Descriptions.Item>
          <Descriptions.Item label="Customer Number">{basicInfo?.custNo}</Descriptions.Item>
          <Descriptions.Item label="Account Number">{basicInfo?.acNo}</Descriptions.Item>
          <Descriptions.Item label="GL Number">{basicInfo?.glNo}</Descriptions.Item>
          <Descriptions.Item label="Cycle">{basicInfo?.cycle?.toString()}</Descriptions.Item>
          <Descriptions.Item label="Interest Rate">{basicInfo?.interestRate}%</Descriptions.Item>
          <Descriptions.Item label="Loan Amount">
            {basicInfo?.loanAmount?.toLocaleString()}
          </Descriptions.Item>
          <Descriptions.Item label="Disbursement Date">
            {basicInfo?.disbursementDate ? dayjs(basicInfo.disbursementDate).format('DD/MM/YYYY') : 'N/A'}
          </Descriptions.Item>
          <Descriptions.Item label="Maturity Date">
            {basicInfo?.maturityDate ? dayjs(basicInfo.maturityDate).format('DD/MM/YYYY') : 'N/A'}
          </Descriptions.Item>
        </Descriptions>
      </Card>

      {/* Balance Summary */}
      <Card title={<><DollarOutlined /> Balance Summary</>}>
        <Row gutter={16}>
          <Col span={6}>
            <Statistic
              title="Outstanding Principal"
              value={balanceSummary?.outstandingPrincipal || 0}
              precision={2}
              prefix="TSh"
            />
          </Col>
          <Col span={6}>
            <Statistic
              title="Outstanding Interest"
              value={balanceSummary?.outstandingInterest || 0}
              precision={2}
              prefix="TSh"
            />
          </Col>
          <Col span={6}>
            <Statistic
              title="Total Outstanding"
              value={balanceSummary?.totalOutstanding || 0}
              precision={2}
              prefix="TSh"
            />
          </Col>
          <Col span={6}>
            <Statistic
              title="Paid Amount"
              value={balanceSummary?.totalPaid || 0}
              precision={2}
              prefix="TSh"
            />
          </Col>
        </Row>
      </Card>

      {/* Arrears Information */}
      {arrearsInfo && (
        <Card title={<><CloseCircleOutlined /> Arrears Information</>}>
          <Row gutter={16}>
            <Col span={8}>
              <Statistic
                title="Days in Arrears"
                value={arrearsInfo.arrearsAgeDays || 0}
                suffix="days"
              />
            </Col>
            <Col span={8}>
              <Statistic
                title="Arrears Amount"
                value={arrearsInfo.totalArrearsAmount || 0}
                precision={2}
                prefix="TSh"
              />
            </Col>
            <Col span={8}>
              <Statistic
                title="Penalty Amount"
                value={arrearsInfo.arrearsPenalty || 0}
                precision={2}
                prefix="TSh"
              />
            </Col>
          </Row>
        </Card>
      )}

      {/* Accrued Interest */}
      {accruedInfo && (
        <Card title={<><PercentageOutlined /> Accrued Interest</>}>
          <Row gutter={16}>
            <Col span={12}>
              <Statistic
                title="Accrued Interest"
                value={accruedInfo.totalAccruedInterest || 0}
                precision={2}
                prefix="TSh"
              />
            </Col>
            <Col span={12}>
              <Statistic
                title="Last Calculation Date"
                value={accruedInfo.lastAccrualDate ? dayjs(accruedInfo.lastAccrualDate).format('DD/MM/YYYY') : 'N/A'}
              />
            </Col>
          </Row>
        </Card>
      )}

      {/* Payment History */}
      {paymentHistory?.recentTransactions && paymentHistory.recentTransactions.length > 0 && (
        <Card title={<><CheckCircleOutlined /> Payment History</>}>
          <Table
            dataSource={paymentHistory.recentTransactions}
            columns={paymentColumns}
            rowKey="paymentId"
            pagination={{ pageSize: 10 }}
            scroll={{ x: 800 }}
          />
        </Card>
      )}

      {/* Loan Schedule */}
      {scheduleInfo?.upcomingPayments && scheduleInfo.upcomingPayments.length > 0 && (
        <Card title={<><CalendarOutlined /> Loan Schedule</>}>
          <Table
            dataSource={scheduleInfo.upcomingPayments}
            columns={scheduleColumns}
            rowKey="scheduleId"
            pagination={{ pageSize: 10 }}
            scroll={{ x: 800 }}
          />
        </Card>
      )}
    </div>
  );
};
